import type {User} from "firebase/auth";

/**
 * Route that hosts the "verify your email" UI (resend modal + oobCode handler).
 * Unverified email/password users are redirected here and held until verified.
 */
export const EMAIL_VERIFICATION_ROUTE = "/email-verification";

/**
 * True when the user has an email/password credential linked. OAuth providers
 * (e.g. Google) report verified emails, so this narrows the gate to accounts
 * created via email + password sign-up.
 */
export function hasPasswordProvider(user: Pick<User, "providerData"> | null | undefined): boolean {
    if (!user) return false;
    return user.providerData?.some((provider) => provider?.providerId === "password") ?? false;
}

/**
 * Central gate predicate: an email/password account that has not yet verified
 * its email address must not be allowed to use the platform.
 *
 * OAuth-only accounts always return false here because Firebase marks their
 * email as verified on sign-in.
 */
export function requiresEmailVerification(user: User | null | undefined): boolean {
    if (!user) return false;
    if (user.emailVerified) return false;
    return hasPasswordProvider(user);
}

/**
 * Prefer the live Auth user once `reload()` has marked it verified.
 * Context can still hold the signup response: `onAuthStateChanged` does not
 * run again when only `emailVerified` changes.
 */
export function authUserForEmailVerification(
    contextUser: User | null | undefined,
    liveUser: User | null | undefined,
): User | null | undefined {
    if (liveUser?.emailVerified) return liveUser;
    return contextUser || liveUser;
}
